// Lazy import PostHog to avoid loading it when disabled
let posthog: typeof import('posthog-js').default | null = null;

const apiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
// Use reverse proxy if available (bypasses ad blockers), otherwise use direct PostHog host
const useProxy = typeof window !== 'undefined' && window.location;
const defaultHost = 'https://us.i.posthog.com';
const host = useProxy 
  ? '/ingest'  // Use reverse proxy through Next.js rewrites
  : (process.env.NEXT_PUBLIC_POSTHOG_HOST || defaultHost);
const enabled = process.env.NEXT_PUBLIC_ENABLE_ANALYTICS === 'true';
// Determine environment from hostname or env var (for filtering in PostHog)
const getEnvironment = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  const hostname = window.location.hostname;
  if (hostname.includes('staging')) return 'staging';
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'local';
  return 'production';
};
const environment = getEnvironment();

// Lazy load PostHog only when needed
async function loadPosthog() {
  if (posthog) return posthog;
  const posthogModule = await import('posthog-js');
  posthog = posthogModule.default;
  return posthog;
}

// PostHog properties type (compatible with analytics data)
type PostHogProperties = Record<string, string | number | boolean | null | undefined>;

// Type guard to check if PostHog is loaded
declare global {
  interface Window {
    posthog?: {
      __loaded?: boolean;
      capture: (event: string, properties?: PostHogProperties) => void;
      captureException?: (error: Error, additionalProperties?: PostHogProperties) => void;
      identify?: (distinctId: string, properties?: PostHogProperties) => void;
      reset?: () => void;
    };
  }
}

export function initPosthog() {
  // Early return if not enabled or no API key
  if (!enabled || !apiKey) {
    console.debug('[PostHog] Not initialized:', { enabled, hasApiKey: !!apiKey });
    return;
  }

  // Early return if not in browser
  if (typeof window === 'undefined') return;

  // Check if PostHog is already initialized
  if (window.posthog && window.posthog.__loaded) {
    return;
  }

  // Initialize PostHog asynchronously to avoid blocking page loads
  // Use requestIdleCallback if available, otherwise setTimeout with a delay
  const init = async () => {
    try {
      const ph = await loadPosthog();
      // If using proxy, we need to set the ui_host to the actual PostHog host for feature flags
      const actualPostHogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || defaultHost;
      ph.init(apiKey, {
        api_host: host,
        ui_host: actualPostHogHost, // Required when using proxy for feature flags
        capture_pageview: true,
        capture_pageleave: true,
        // Enable exception autocapture to automatically track JavaScript errors
        capture_exceptions: true,
        // Add timeout to prevent hanging
        request_batching: true,
        // Disable autocapture in test environments to avoid blocking
        autocapture: process.env.NODE_ENV === 'production',
        // Set environment for filtering in PostHog (matches backend POSTHOG_ENVIRONMENT)
        loaded: (phInstance) => {
          // Set environment property on all events
          phInstance.register({
            environment: environment,
          });
          // Mark as loaded and expose on window with proper typing
          window.posthog = {
            __loaded: true,
            capture: (event: string, properties?: PostHogProperties) => {
              phInstance.capture(event, properties);
            },
            captureException: (error: Error, additionalProperties?: PostHogProperties) => {
              // Use PostHog's built-in captureException method (available in posthog-js v1.207.8+)
              if (typeof phInstance.captureException === 'function') {
                phInstance.captureException(error, additionalProperties);
              } else {
                // Fallback for older SDK versions
                phInstance.capture("$exception", {
                  $exception_type: error.name || "Error",
                  $exception_message: error.message,
                  $exception_stack_trace_raw: error.stack,
                  ...additionalProperties,
                });
              }
            },
            identify: (distinctId: string, properties?: PostHogProperties) => {
              phInstance.identify(distinctId, properties);
            },
            reset: () => {
              phInstance.reset();
            },
          };
          console.debug('[PostHog] Initialized successfully', { api_host: host, ui_host: actualPostHogHost });
        },
      });
    } catch (error) {
      // Log error for debugging (but don't break the app)
      console.error('Failed to initialize PostHog:', error);
    }
  };

  if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
    requestIdleCallback(() => {
      init();
    }, { timeout: 2000 });
  } else {
    // Fallback: initialize after a short delay to avoid blocking page load
    setTimeout(() => {
      init();
    }, 100);
  }
}

// Export a getter that returns posthog if loaded, null otherwise
export function getPosthog() {
  return posthog;
}

